import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../utils/api';

const StatCard = ({ label, value, sub, color = 'text-white', accent }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
    <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    {accent && <p className="text-xs mt-1" style={{ color: '#4F6AFF' }}>{accent}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-400">Loading dashboard...</div>;
  if (!data) return <div className="text-red-400">Failed to load dashboard</div>;

  const hoursSavedNum = parseFloat(data.hoursSaved || 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <span className="text-xs text-gray-500">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
      </div>

      {/* Primary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="Total Leads" value={data.totalLeads} color="text-blue-400" />
        <StatCard label="Emails Today" value={`${data.emailsSentToday} / ${data.dailyCap}`} color="text-green-400" sub={`${data.emailsSentWeek} this week`} />
        <StatCard label="Emails This Month" value={data.emailsSentMonth} color="text-white" />
        <StatCard label="Reply Rate" value={`${data.replyRate}%`} color="text-purple-400" sub={`${data.totalReplied} total replies`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="Replied This Month" value={data.repliedThisMonth} color="text-purple-400" />
        <StatCard label="Hot Leads" value={data.hotLeads} color="text-red-400" />
        <StatCard label="Called" value={data.calledCount} color="text-cyan-400" />
        <StatCard label="Booked Calls" value={data.bookedCount} color="text-emerald-400" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Follow Ups Pending" value={data.followUpsPending} color="text-orange-400" />
        <StatCard
          label="Hours Saved This Month"
          value={`${data.hoursSaved}h`}
          color="text-blue-300"
          sub={`Worth $${data.labourCost} in labour`}
        />
        <StatCard
          label="After Hours Activity"
          value={data.afterHoursMonth}
          color="text-indigo-400"
          sub="Emails sent outside 9-5 AEST"
        />
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">System Cost</p>
          <p className="text-sm font-bold text-white">ZJ: $397/mo</p>
          <p className="text-sm font-bold text-green-400">You'd pay: ${data.labourCost}/mo</p>
          {data.labourCost > 397 && (
            <p className="text-xs text-green-500 mt-1">Saving ${data.labourCost - 397}/mo</p>
          )}
        </div>
      </div>

      {/* Hours saved banner */}
      {data.emailsSentMonth > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6 flex items-center gap-4">
          <div className="text-2xl">⚡</div>
          <div>
            <p className="text-sm font-semibold text-blue-300">
              This month the system saved you {data.hoursSaved} hours worth ${data.labourCost} in labour
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              ZJ system: $397/mo | Human equivalent: ${data.labourCost}/mo
            </p>
          </div>
        </div>
      )}

      {/* Follow Up Reminders */}
      {data.followUpReminders && data.followUpReminders.length > 0 && (
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-5 mb-6">
          <h3 className="text-lg font-semibold mb-3 text-orange-400">Follow Up Reminders</h3>
          <p className="text-xs text-gray-500 mb-3">These prospects were emailed {data.followUpReminders[0]?.daysSince}+ days ago with no reply</p>
          <div className="space-y-2">
            {data.followUpReminders.map(r => (
              <div key={r.id} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{r.businessName}</p>
                  <p className="text-xs text-gray-500">{r.email} &middot; {r.serviceGap}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-orange-400 font-medium">{r.daysSince} days ago</p>
                  <p className="text-xs text-gray-500">Follow up #{r.followUpCount + 1}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-4">Emails Sent This Week</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.emailsPerDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#9ca3af' }}
              />
              <Bar dataKey="count" fill="#4F6AFF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          {data.recentEmails.length === 0 ? (
            <p className="text-gray-500 text-sm">No emails sent yet</p>
          ) : (
            <div className="space-y-3 max-h-[240px] overflow-y-auto">
              {data.recentEmails.map((email, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{email.businessName}</p>
                      {email.afterHours && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400">After Hours</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate max-w-[180px]">{email.subject}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">
                      {new Date(email.sentAt).toLocaleDateString('en-AU')}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${email.type === 'follow-up' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {email.type === 'follow-up' ? 'Follow Up' : 'Initial'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
